/**
 * Spa Booking Widget JavaScript
 * Handles datepicker initialization and API interactions
 */

/* global flatpickr */

// Global In-Memory Caches for instant UI updates and background prefetching
// Note: This cache is strictly per-device/browser session in RAM, not shared across users or devices.
const datesCache = new Map(); // key -> { data: availableDates, timestamp: number }
const timesCache = new Map(); // key -> { data: availableTimes, timestamp: number }
const inFlightDateRequests = new Map(); // key -> Promise
const inFlightTimeRequests = new Map(); // key -> Promise

const DATES_CACHE_TTL_MS = 5 * 60 * 1000; // 5 minutes TTL for month dates
const TIMES_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutes TTL for specific day time slots

window.clearBookingCache = function () {
  datesCache.clear();
  timesCache.clear();
  inFlightDateRequests.clear();
  inFlightTimeRequests.clear();
  console.log('🧹 [Booking Cache Cleared]');
};

function getDatesCacheKey(centerId, serviceId, monthYear) {
  return `${centerId || ''}_${serviceId || ''}_${monthYear || ''}`;
}

function getTimesCacheKey(centerId, serviceId, date) {
  return `${centerId || ''}_${serviceId || ''}_${date || ''}`;
}

function getAdjacentMonthYear(monthYearStr, offset = 1) {
  if (!monthYearStr || !monthYearStr.includes('-')) {
    const d = new Date();
    d.setMonth(d.getMonth() + offset);
    return `${d.getMonth() + 1}-${d.getFullYear()}`;
  }
  const parts = monthYearStr.split('-');
  const month = parseInt(parts[0], 10);
  const year = parseInt(parts[1], 10);
  const d = new Date(year, month - 1 + offset, 1);
  return `${d.getMonth() + 1}-${d.getFullYear()}`;
}

class SpaBookingWidget {
  constructor(selectedService, selectedCenter, dataUrl, isNewPatient = false) {
    this.selectedService = selectedService;
    this.selectedCenter = selectedCenter;
    this.dataUrl = dataUrl;
    this.isNewPatient = isNewPatient;

    console.log(`SpaBookingWidget initialized with patient type: ${isNewPatient ? 'New Patient' : 'Returning Patient'}`);
    this.dateInput = null;
    this.availableTimesContainer = document.getElementById('available-times-container');
    this.availableTimes = document.getElementById('available-times');
    this.loadingDates = document.getElementById('loading-dates');
    this.loadingTimes = document.getElementById('loading-times');
    this.selectedDate = null;
    this.selectedTime = null;

    // Initialize global center state if not already set
    if (selectedCenter && !window.selectedCenter) {
      window.selectedCenter = selectedCenter;
      console.log('Center initialized from widget constructor:', window.selectedCenter);
    }

    this.init();

    // Store instance globally for access from booking-journey
    window.spaBookingWidgetInstance = this;
  }

  init() {
    console.log('SpaBookingWidget initialized with service:', this.selectedService);
    console.log('dataUrl', this.dataUrl);
    console.log('zenoti guest', window.zenotiGuest);
    if (!document.getElementById('appointment-date')) {
      console.warn('Spa booking widget: appointment-date element not found');
      return;
    }

    // Hide treatment-toggle for new patients
    this.updateTreatmentToggleVisibility();

    this.initializeDatePicker();
    this.setupLocationHandlers();
    this.setupTimeSelectionHandlers();
    this.fetchInitialAvailableDates();

    // Update button state with initialized center
    if (typeof window.updateBookButtonState === 'function') {
      window.updateBookButtonState();
    }
  }

  initializeDatePicker() {
    const dateElement = document.getElementById('appointment-date');

    if (!dateElement) {
      console.error('❌ appointment-date element not found');
      return;
    }

    this.dateInput = flatpickr(dateElement, {
      disableMobile: true,
      minDate: "today",
      dateFormat: "Y-m-d",

      onChange: (selectedDates, dateStr) => {
        console.log('📅 DATE SELECTED:', dateStr);
        this.handleDateChange(dateStr);
      },

      onMonthChange: (selectedDates, dateStr, instance) => {
        console.log(
          '📆 MONTH CHANGED:',
          instance.currentMonth + 1,
          instance.currentYear
        );

        this.handleMonthChange(instance);
      },

      onYearChange: (selectedDates, dateStr, instance) => {
        console.log(
          '📆 YEAR CHANGED:',
          instance.currentMonth + 1,
          instance.currentYear
        );

        this.handleMonthChange(instance);
      }
    });

    console.log('✅ Datepicker initialized');
  }

  handleDateChange(selectedDate) {
    if (!selectedDate) {
      this.selectedDate = null;
      this.updateBookButtonState();
      return;
    }

    this.selectedDate = selectedDate;
    console.log('Date selected:', selectedDate);
    this.updateBookButtonState();

    const location = this.getSelectedLocation();
    if (!location) {
      console.warn('No location selected');
      return;
    }

    this.fetchAvailableTimes(location, selectedDate);
  }

  handleMonthChange(instance) {
    const { currentMonth, currentYear } = instance;
    const monthYear = `${currentMonth + 1}-${currentYear}`;
    const location = this.getSelectedLocation();

    // Clear previous selection and times when changing month
    this.selectedDate = null;
    this.selectedTime = null;
    this.resetAvailableTimes();
    this.updateBookButtonState();

    if (location) {
      this.fetchAvailableDates(location, monthYear);
    }
  }

  async fetchAvailableTimes(location, date) {
    const centerId = location;
    const serviceId = this.getServiceId();
    const cacheKey = getTimesCacheKey(centerId, serviceId, date);

    // 1. Instant Cache check
    const cached = timesCache.get(cacheKey);
    const isCacheValid = cached && (Date.now() - cached.timestamp < TIMES_CACHE_TTL_MS);

    if (isCacheValid) {
      console.log(`⚡ [Instant Cache Hit] Available times for ${date}`);
      this.renderAvailableTimes(cached.data);
      return;
    }

    this.showLoading('times');

    try {
      let fetchPromise = inFlightTimeRequests.get(cacheKey);
      if (!fetchPromise) {
        fetchPromise = (async () => {
          const response = await fetch(
            `/apps/spa-booking/api/appointments/available-times?center_id=${centerId}&service_id=${serviceId}&date=${date}`
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const availableTimes = data?.availableTimes || [];
          timesCache.set(cacheKey, { data: availableTimes, timestamp: Date.now() });
          return availableTimes;
        })();

        inFlightTimeRequests.set(cacheKey, fetchPromise);
        fetchPromise.finally(() => inFlightTimeRequests.delete(cacheKey));
      }

      const availableTimes = await fetchPromise;
      this.renderAvailableTimes(availableTimes);
    } catch (error) {
      console.error('Error fetching available times:', error);
      this.renderAvailableTimes([]);
    } finally {
      this.hideLoading('times');
    }
  }

  async fetchAvailableDates(location, monthYear, isPrefetch = false) {
    if (!location) {
      console.warn('Cannot fetch available dates: location is not provided');
      return [];
    }

    const centerId = location;
    const serviceId = this.getServiceId();
    const cacheKey = getDatesCacheKey(centerId, serviceId, monthYear);

    // 1. Instant Cache check
    const cached = datesCache.get(cacheKey);
    const isCacheValid = cached && (Date.now() - cached.timestamp < DATES_CACHE_TTL_MS);

    if (isCacheValid) {
      console.log(`⚡ [Instant Cache Hit] Available dates for ${monthYear} (key: ${cacheKey})`);
      if (!isPrefetch) {
        this.updateAvailableDates(cached.data);
        // Pre-load next month in background if not already cached
        this.prefetchAdjacentMonths(centerId, serviceId, monthYear);
      }
      return cached.data;
    }

    // 2. Show spinner only if user is actively waiting
    if (!isPrefetch) {
      this.showLoading('dates');
      const noticeEl = document.getElementById('no-dates-notice');
      if (noticeEl) {
        noticeEl.style.display = 'none';
      }
    }

    try {
      // 3. Request deduplication
      let fetchPromise = inFlightDateRequests.get(cacheKey);
      if (!fetchPromise) {
        fetchPromise = (async () => {
          const response = await fetch(
            `/apps/spa-booking/api/appointments/available-dates?center_id=${centerId}&service_id=${serviceId}&month-year=${monthYear}`
          );

          if (!response.ok) {
            throw new Error(`HTTP error! status: ${response.status}`);
          }

          const data = await response.json();
          const availableDates = data?.availableDates || [];
          if (availableDates.length > 0) {
            datesCache.set(cacheKey, { data: availableDates, timestamp: Date.now() });
          }
          return availableDates;
        })();

        inFlightDateRequests.set(cacheKey, fetchPromise);
        fetchPromise.finally(() => inFlightDateRequests.delete(cacheKey));
      }

      const availableDates = await fetchPromise;

      if (!isPrefetch) {
        this.updateAvailableDates(availableDates);
        // Proactively prefetch adjacent months in background
        this.prefetchAdjacentMonths(centerId, serviceId, monthYear);
      }

      return availableDates;
    } catch (error) {
      console.error(`Error fetching available dates for ${monthYear}:`, error);
      if (!isPrefetch) {
        this.updateAvailableDates([]);
      }
      return [];
    } finally {
      if (!isPrefetch) {
        this.hideLoading('dates');
      }
    }
  }

  prefetchAdjacentMonths(centerId, serviceId, currentMonthYear) {
    if (!centerId || !serviceId || !currentMonthYear) return;

    const nextMonthYear = getAdjacentMonthYear(currentMonthYear, 1);
    const nextNextMonthYear = getAdjacentMonthYear(currentMonthYear, 2);

    // Non-blocking prefetch of next month
    const nextKey = getDatesCacheKey(centerId, serviceId, nextMonthYear);
    if (!datesCache.has(nextKey) && !inFlightDateRequests.has(nextKey)) {
      console.log(`🚀 [Prefetching] Next month: ${nextMonthYear}`);
      this.fetchAvailableDates(centerId, nextMonthYear, true).catch(() => { });
    }

    // Non-blocking prefetch of 2 months ahead after short delay
    const nextNextKey = getDatesCacheKey(centerId, serviceId, nextNextMonthYear);
    if (!datesCache.has(nextNextKey) && !inFlightDateRequests.has(nextNextKey)) {
      setTimeout(() => {
        this.fetchAvailableDates(centerId, nextNextMonthYear, true).catch(() => { });
      }, 300);
    }
  }

  renderAvailableTimes(times) {
    if (!times.length) {
      this.availableTimes.innerHTML = '<p>No available times for this date.</p>';
      this.availableTimesContainer.style.display = 'block';
      return;
    }

    this.availableTimes.innerHTML = '';

    times.forEach((slot) => {
      const timeSlotElement = this.createTimeSlotElement(slot);
      this.availableTimes.appendChild(timeSlotElement);
    });

    this.availableTimesContainer.style.display = 'block';
  }

  createTimeSlotElement(slot) {
    const container = document.createElement('div');
    container.className = 'time-slot';

    const radioButton = document.createElement('input');
    radioButton.type = 'radio';
    radioButton.name = 'appointment-time';
    radioButton.value = slot.startISO;
    radioButton.id = `time-${slot.startISO}`;

    const label = document.createElement('label');
    label.textContent = `${slot.start} - ${slot.end}`;
    label.htmlFor = `time-${slot.startISO}`;

    container.appendChild(radioButton);
    container.appendChild(label);

    return container;
  }

  setupTimeSelectionHandlers() {
    // Use event delegation to handle time slot selection
    this.availableTimes.addEventListener('change', (event) => {
      if (event.target.name === 'appointment-time') {
        this.handleTimeSelection(event.target);
      }
    });
  }

  handleTimeSelection(radioButton) {
    // Get the time as displayed in the UI (e.g., "3:30 PM")
    const timeLabel = radioButton.nextElementSibling.textContent;
    // Extract just the start time (before the dash)
    const startTime = timeLabel.split(' - ')[0];

    this.selectedTime = startTime;
    console.log('Time selected:', {
      isoTime: radioButton.value,
      displayTime: startTime,
      fullLabel: timeLabel
    });
    this.updateBookButtonState();
  }

  updateBookButtonState() {
    // Update global state for the booking journey
    window.selectedDate = this.selectedDate;
    window.selectedTime = this.selectedTime;

    // Call the global update function from booking-journey if it exists
    if (typeof window.updateBookButtonState === 'function') {
      window.updateBookButtonState();
    }

    console.log('Book button state updated:', {
      date: this.selectedDate,
      time: this.selectedTime,
      center: this.selectedCenter,
      canBook: !!(this.selectedDate && this.selectedTime && this.selectedCenter)
    });
  }

  updateAvailableDates(availableDates) {
    const dates = (availableDates || []).map(date => date.date).filter(Boolean);
    console.log('✅ Available dates from API:', dates);

    const noticeEl = document.getElementById('no-dates-notice');

    if (!this.dateInput) return;

    // Reset disable rules so they never permanently block dates
    this.dateInput.set('disable', []);

    if (dates.length === 0) {
      // In Flatpickr, setting enable to [] disables all dates in the currently viewed month
      // WITHOUT resetting or jumping back to the initial month!
      this.dateInput.set('enable', []);
      this.dateInput.redraw();

      if (noticeEl) {
        const centerName = window.centerNames?.[this.selectedCenter] || 'this location';
        noticeEl.textContent = `No available appointment dates found for ${centerName} in this month. Please select another clinic or check another month.`;
        noticeEl.style.display = 'block';
      }
      return;
    }

    if (noticeEl) {
      noticeEl.style.display = 'none';
      noticeEl.textContent = '';
    }

    // Enable only available dates from API
    this.dateInput.set('enable', dates);
    this.dateInput.redraw();
  }

  setupLocationHandlers() {
    const centerSelect = document.querySelector('#center-select');
    if (centerSelect) {
      centerSelect.addEventListener('change', () => {
        const val = centerSelect.value;
        // Sync with hidden/visible radio buttons
        const matchingRadio = document.querySelector(`input[name="center"][value="${val}"]`);
        if (matchingRadio) {
          matchingRadio.checked = true;
          document.querySelectorAll('input[name="center"]').forEach(r => {
            const label = document.querySelector(`label[for="${r.id}"]`);
            if (label) {
              if (r.checked) label.classList.add('checked');
              else label.classList.remove('checked');
            }
          });
        }
        this.handleLocationChange();
      });
    }

    const centerRadios = document.querySelectorAll('input[name="center"]');
    centerRadios.forEach(radio => {
      radio.addEventListener('change', () => {
        if (radio.checked) {
          if (centerSelect && centerSelect.value !== radio.value) {
            centerSelect.value = radio.value;
          }
          this.handleLocationChange();
        }
      });
    });
  }

  //   setupLocationHandlers() {
  //   const centerRadios = document.querySelectorAll('input[name="center"]');

  //   if (!centerRadios.length) {
  //     console.warn('No center radio buttons found');
  //     return;
  //   }

  //   centerRadios.forEach(radio => {
  //     radio.addEventListener('change', () => {
  //       this.handleLocationChange();
  //     });
  //   });

  //   console.log(`✅ ${centerRadios.length} center radio buttons initialized`);
  // }


  // handleLocationChange() {
  //   const selectedLocation = this.getSelectedLocation();
  //   console.log('Selected Location:', selectedLocation);
  //   this.selectedCenter = selectedLocation;
  //   window.selectedCenter = selectedLocation;
  //   // Reset datepicker and times
  //   this.dateInput.clear();
  //   this.resetAvailableTimes();
  //   this.selectedDate = null;
  //   this.selectedTime = null;
  //   this.updateBookButtonState();

  //   // Fetch new dates for the current month
  //   if (selectedLocation) {
  //     window.selectedCenter = selectedLocation;
  //     const today = new Date();
  //     const currentMonthYear = `${today.getMonth() + 1}-${today.getFullYear()}`;
  //     this.fetchAvailableDates(selectedLocation, currentMonthYear);
  //   }
  // }

  handleLocationChange() {
    const selectedLocation = this.getSelectedLocation();

    console.log('📍 Selected Location:', selectedLocation);

    if (!selectedLocation) {
      console.warn('No location selected');
      return;
    }

    this.selectedCenter = selectedLocation;
    window.selectedCenter = selectedLocation;

    // Reset date and time
    if (this.dateInput) {
      this.dateInput.clear();
    }

    this.resetAvailableTimes();

    this.selectedDate = null;
    this.selectedTime = null;

    this.updateBookButtonState();

    // Check if Skin Bar is selected for an ineligible service
    if (selectedLocation === 'b419772b-0c99-4bdc-b171-0a2250ef045f' && isSkinBarIneligible(this.selectedService)) {
      const noticeEl = document.getElementById('no-dates-notice');
      if (noticeEl) {
        noticeEl.textContent = "This service is not available at Skin Bar by Remedy. Please select Uptown BGC Clinic or Vertis North Clinic.";
        noticeEl.style.display = 'block';
      }
      if (this.dateInput) {
        this.dateInput.set('disable', []);
        this.dateInput.set('enable', []);
        this.dateInput.redraw();
      }
      return;
    }

    // Fetch available dates for selected center
    const today = new Date();
    const currentMonthYear =
      `${today.getMonth() + 1}-${today.getFullYear()}`;

    console.log(
      `Fetching available dates for center ${selectedLocation}, month ${currentMonthYear}`
    );

    this.fetchAvailableDates(
      selectedLocation,
      currentMonthYear
    );
  }

  fetchInitialAvailableDates() {
    const location = this.getSelectedLocation();

    // Direct check for Skin Bar ineligibility
    if (location === 'b419772b-0c99-4bdc-b171-0a2250ef045f' && isSkinBarIneligible(this.selectedService)) {
      const noticeEl = document.getElementById('no-dates-notice');
      if (noticeEl) {
        noticeEl.textContent = "This service is not available at Skin Bar by Remedy. Please select Uptown BGC Clinic or Vertis North Clinic.";
        noticeEl.style.display = 'block';
      }
      if (this.dateInput) {
        this.dateInput.set('disable', []);
        this.dateInput.set('enable', []);
        this.dateInput.redraw();
      }
      return;
    }

    const today = new Date();
    const currentMonthYear = `${today.getMonth() + 1}-${today.getFullYear()}`;

    console.log(`Fetching available dates for ${location}, month-year: ${currentMonthYear}`);
    console.log(`Patient type: ${this.isNewPatient ? 'New Patient' : 'Returning Patient'}`);

    // Fetch available dates
    this.fetchAvailableDates(location, currentMonthYear);
  }

  getSelectedLocation() {

    // Then try to use the Location radio buttons (legacy approach)
    const selectedLocation = document.querySelector('#center-select');
    //const selectedLocation = document.querySelector('input[name="Location"]:checked');
    if (selectedLocation) {
      return selectedLocation.value;
    }


    // Default to the main zenoti centerId (Uptown BGC)
    console.log('Using default center: b877b429-d984-4eee-8e14-83f5a4dd5348');
    return "b877b429-d984-4eee-8e14-83f5a4dd5348";
  }

  //   getSelectedLocation() {
  //   const selectedRadio = document.querySelector(
  //     'input[name="center"]:checked'
  //   );

  //   if (selectedRadio) {
  //     console.log('Selected center radio:', selectedRadio.value);
  //     return selectedRadio.value;
  //   }

  //   // Fallback
  //   console.log(
  //     'Using default center: b877b429-d984-4eee-8e14-83f5a4dd5348'
  //   );

  //   return "b877b429-d984-4eee-8e14-83f5a4dd5348";
  // }


  getCenterId() {
    // Use the selectedCenter passed to constructor, or fall back to location selection
    if (this.selectedCenter) {
      return this.selectedCenter;
    }

    // Fallback to location radio buttons if no center passed
    const location = this.getSelectedLocation();

    // Handle both old location values and direct center IDs
    const centerIdMap = {
      'branch1': 'b877b429-d984-4eee-8e14-83f5a4dd5348', // Uptown BGC
      'branch2': '5f7ea61e-740f-48bf-9e5b-c2a4129d5698', // Vertis
      'branch3': 'b419772b-0c99-4bdc-b171-0a2250ef045f', // Skin Bar GH Mall
      // If already a center ID, use as-is
      'b877b429-d984-4eee-8e14-83f5a4dd5348': 'b877b429-d984-4eee-8e14-83f5a4dd5348',
      '5f7ea61e-740f-48bf-9e5b-c2a4129d5698': '5f7ea61e-740f-48bf-9e5b-c2a4129d5698',
      'b419772b-0c99-4bdc-b171-0a2250ef045f': 'b419772b-0c99-4bdc-b171-0a2250ef045f'
    };

    return centerIdMap[location] || 'b877b429-d984-4eee-8e14-83f5a4dd5348'; // Default to Uptown BGC
  }

  getServiceId() {
    return SERVICE_ID_MAP[this.selectedService] || '9557b111-db0b-44dc-a834-e64f21cdd357'; // Default to Online Consultation
  }

  resetAvailableTimes() {
    this.availableTimes.innerHTML = '';
    this.availableTimesContainer.style.display = 'none';
    this.selectedTime = null;
    this.updateBookButtonState();
  }

  // Method to reset the entire widget when switching services
  resetWidget(newSelectedService, newSelectedCenter, newDataUrl, isNewPatient = this.isNewPatient) {
    console.log('Resetting widget for new service:', newSelectedService, 'center:', newSelectedCenter);
    console.log(`Patient type: ${isNewPatient ? 'New Patient' : 'Returning Patient'}`);

    // Update service info
    this.selectedService = newSelectedService;
    toggleCentersForHairAnalyzer(newSelectedService);
    this.selectedCenter = newSelectedCenter;
    this.dataUrl = newDataUrl;
    this.isNewPatient = isNewPatient;

    // Hide treatment-toggle for new patients
    this.updateTreatmentToggleVisibility();

    // Log service ID for debugging
    const serviceId = this.getServiceId();
    console.log(`Service ID for "${newSelectedService}": ${serviceId}`);

    // Clear the datepicker
    if (this.dateInput) {
      this.dateInput.clear();
      this.dateInput.set('disable', []); // Remove all disabled dates
    }

    // Reset available times and selections
    this.resetAvailableTimes();
    this.selectedDate = null;
    this.updateBookButtonState();

    // For Navbar Returning Patient, use default data
    if (!this.isNewPatient) {
      const today = new Date();
      const currentMonthYear = `${today.getMonth() + 1}-${today.getFullYear()}`;
      const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`;

      console.log('Returning Patient - Using default dates and times');

      // Generate default dates and times
      const defaultDates = this.getDefaultAvailableDates(currentMonthYear);
      const defaultTimes = this.getDefaultAvailableTimes(todayStr);
      console.log('Default dates:', defaultDates);
      console.log('Default times:', defaultTimes);
      // Update available dates in the datepicker
      this.updateAvailableDates(defaultDates);

      // Pre-select today's date
      this.dateInput.setDate(todayStr);
      this.selectedDate = todayStr;

      // Render available times for today
      this.renderAvailableTimes(defaultTimes);

      return;
    }

    // For New Patient, make API calls
    console.log('New Patient - Fetching data from API');
    console.log('Fetching initial available dates from resetWidget');

    // Log the center ID that will be used
    console.log(`Center ID from this.selectedCenter: ${this.selectedCenter}`);
    console.log(`Center ID from window.selectedCenter: ${window.selectedCenter}`);
    console.log(`Center ID from getSelectedLocation(): ${this.getSelectedLocation()}`);

    // Use the selected center directly for fetching dates
    const today = new Date();
    const currentMonthYear = `${today.getMonth() + 1}-${today.getFullYear()}`;

    // Use the center ID from this.selectedCenter directly
    if (this.selectedCenter) {
      console.log(`Directly fetching dates for center: ${this.selectedCenter}`);
      this.fetchAvailableDates(this.selectedCenter, currentMonthYear);
    } else {
      // Fall back to the normal method if no center is set
      this.fetchInitialAvailableDates();
    }
  }

  showLoading(type) {
    if (type === 'dates') {
      this.loadingDates.style.display = 'inline-block';
    } else if (type === 'times') {
      this.loadingTimes.style.display = 'inline-block';
    }
  }

  hideLoading(type) {
    if (type === 'dates') {
      this.loadingDates.style.display = 'none';
    } else if (type === 'times') {
      this.loadingTimes.style.display = 'none';
    }
  }

  showError(message) {
    // You could implement a more sophisticated error display
    console.error(message);
    // Optionally show user-friendly error message
  }

  // Method to update treatment toggle visibility based on patient type
  updateTreatmentToggleVisibility() {
    //#options_treatment_new (only show the three values on "Treatments (No Consultation Required)")
    //#options_treatment (show all other times)

    const treatmentToggle = document.getElementById('treatment-toggle');
    const optionsTreatmentNew = document.getElementById('options_treatment_new')
    const optionsTreatment = document.getElementById('options_treatment')
    console.log({ dataUrl: this.dataUrl, optionsTreatmentNew, optionsTreatment })

    const isBookWithoutTreatment = this.dataUrl?.includes("book-treatment-without") ? true : false;
    const isBookATreatment = this.dataUrl?.includes("book-a-treatment") ? true : false;

    if (isBookWithoutTreatment) {
      treatmentToggle.style.display = 'block';
      optionsTreatmentNew.style.display = 'block';
      optionsTreatment.style.display = 'none';
      return
    }
    if (isBookATreatment) {
      treatmentToggle.style.display = 'block';
      optionsTreatmentNew.style.display = 'none';
      optionsTreatment.style.display = 'block';
      return
    }

    //default
    treatmentToggle.style.display = 'none';
    optionsTreatmentNew.style.display = 'none';
    optionsTreatment.style.display = 'none';

    return
    // const treatmentToggle = document.getElementById('treatment-toggle');
    // if (treatmentToggle && this.dataUrl) {
    //   // Hide for new patients, show for returning patients
    //   if (this.dataUrl && this.dataUrl.includes('new-patient')) {
    //     console.log('Hiding treatment toggle for new patient');
    //     treatmentToggle.style.display = 'none';
    //   } else {
    //     console.log('Showing treatment toggle for returning patient');
    //     treatmentToggle.style.display = 'block';
    //   }
    // }
    // console.log("UpdateTreatmentToggleVisiblity", {treatmentToggle, dataUrl: this.dataUrl})
  }

  // Helper method to generate default available dates for New Patient flow
  getDefaultAvailableDates(monthYear) {
    console.log('Generating default dates for month-year:', monthYear);

    const [month, year] = monthYear.split('-').map(Number);
    const today = new Date();
    const currentMonth = today.getMonth() + 1;
    const currentYear = today.getFullYear();

    // Generate dates for the next 30 days if we're in the current month
    // or for the whole month if it's a future month
    const dates = [];
    const daysInMonth = new Date(year, month, 0).getDate();
    const startDay = (month === currentMonth && year === currentYear) ? today.getDate() : 1;

    for (let day = startDay; day <= daysInMonth; day++) {
      const date = `${year}-${String(month).padStart(2, '0')}-${String(day).padStart(2, '0')}`;
      dates.push({ date });
    }

    return dates;
  }

  // Helper method to generate default available times for New Patient flow
  getDefaultAvailableTimes(date) {
    console.log('Generating default times for date:', date);

    // Parse date manually to avoid timezone issues
    const [year, month, day] = date.split('-').map(Number);
    const selectedDate = new Date(year, month - 1, day); // month is 0-indexed
    const dayOfWeek = selectedDate.getDay();

    console.log('Parsed date:', selectedDate, 'Day of week:', dayOfWeek, '(0=Sunday, 6=Saturday)');

    // Skip weekends (0 = Sunday, 6 = Saturday)
    if (dayOfWeek === 0 || dayOfWeek === 6) {
      console.log('Skipping weekend date');
      return [];
    }

    // Generate time slots from 9 AM to 5 PM with 30-minute intervals
    const times = [];

    // Generate time slots
    for (let hour = 9; hour < 19; hour++) {
      for (let minute = 0; minute < 60; minute += 30) {
        const startHour = hour;
        const endHour = minute === 30 ? hour + 1 : hour;
        const endMinute = minute === 30 ? 0 : 30;

        const start = `${String(startHour).padStart(2, '0')}:${String(minute).padStart(2, '0')}`;
        const end = `${String(endHour).padStart(2, '0')}:${String(endMinute).padStart(2, '0')}`;

        // ISO format for the startISO field
        const startISO = `${date}T${start}:00`;

        times.push({
          start,
          end,
          startISO
        });
      }
    }

    console.log('Generated', times.length, 'time slots');
    return times;
  }
}

// Expose the class globally so it can be called from other scripts
window.SpaBookingWidget = SpaBookingWidget;

// Initialize when DOM is ready
document.addEventListener('DOMContentLoaded', function () {
  // Check if the booking widget exists on the page
  if (document.getElementById('booking-section')) {
    new SpaBookingWidget();
  }
});

// Center selection will be initialized in initBookingJourney()

// ============================================================================
// BOOKING JOURNEY LOGIC (moved from booking-journey.liquid)
// ============================================================================

// Global variables to track selected date, time, center, and patient type
window.selectedDate = null;
window.selectedTime = null;
window.selectedCenter = "b877b429-d984-4eee-8e14-83f5a4dd5348"; // Default to Uptown BGC

window.centerNames = {
  "b877b429-d984-4eee-8e14-83f5a4dd5348": "Uptown BGC",
  "5f7ea61e-740f-48bf-9e5b-c2a4129d5698": "Vertis",
  "b419772b-0c99-4bdc-b171-0a2250ef045f": "Skin Bar GH Mall"
};
window.defaultCenterId = "b877b429-d984-4eee-8e14-83f5a4dd5348";

// Product ID mappings for services - UPDATED WITH CORRECT VARIANT ID
// These are actually the Variant id values
const SERVICE_PRODUCT_MAP = {
  "Doctor's Consultation (In Person)": 42424520605759,  //productId 7916378161215
  'In Person Doctors Appointment': 42424520605759,
  'In Person Follow Up': 42424520605759,

  "Doctor's Consultation (Online)": 32153038848063,   //productId Remote Consultation 4570608336959
  'Virtual Consultation': 32153038848063,
  'Online Follow Up': 32153038848063,
  'Treatments/Combos/Packages': 32153038848063,
  'Remote Consultation': 32153038848063,

  // 'Book A Treatment W/O Consult' (variant 42425124192319, PHP 1,500) is a placeholder and must never be sold.
  // No-consultation bookings always use the chosen treatment's own product (see getSelectedService).
  // Removed 2026-10-22 after orders #6291 and #6316 were charged PHP 1,500 for a PHP 7,500 treatment.

  'Skin Analyzer': 42860716851263,  //productId 7916378161215
  'Hair Analyzer': 42863074443327, // 8076051349567

  'Hydraboost': 41827279077439,  //productId 7832291639359
  'Skin Lift': 41827345596479,  //productId 7832326242367
  'Skin Refresh': 41827335471167, //productId 7832318935103

  'Book A Treatment': -5,
  'Follow Up Consultation (In Person)': -4,
  'Follow Up Consultation (Online)': -3,
  'Follow Up Consultation (Skin Analyzer)': -2,
  '595 Laser': 1,
  'Acne Cleanse': 2,
  'Back Peel': 3,
  'Bidens': 4,
  'Body Cleanse': 5,
  'Body Slim': 6,
  'Bodybrite': 7,
  'BodyBrite Peel': 8,
  'Carbon Laser Facial': 9,
  'Cortisone Shots': 10,
  'Custom Peel': 11,
  'Electrolase': 12,
  'Essential Cleanse': 13,
  'Exoskin': 14,
  'Face Filter': 15,
  'Faux Fascial': 16,
  'Fresh Eyes': 17,
  'Glass Skin Facial': 18,
  'Hollywood Laser Facial': 19,
  'Laser Hair Removal': 21
};

// Treatment handle to service name mapping
const TREATMENT_SERVICE_MAP = {
  '595-laser': '595 Laser',
  'acne-cleanse-1': 'Acne Cleanse',
  'back-peel': 'Back Peel',
  'bidens': 'Bidens',
  'body-cleanse': 'Body Cleanse',
  'body-slim': 'Body Slim',
  'bodybrite': 'Bodybrite',
  'bodybrite-peel': 'BodyBrite Peel',
  'carbon-laser-facial-1': 'Carbon Laser Facial',
  'cortisone-shots': 'Cortisone Shots',
  'custom-peel': 'Custom Peel',
  'electrolase': 'Electrolase',
  'essential-cleanse': 'Essential Cleanse',
  'exoskin': 'Exoskin',
  'face-filter': 'Face Filter',
  'faux-fascial': 'Faux Fascial',
  'fresh-eyes': 'Fresh Eyes',
  'glass-skin-facial-1': 'Glass Skin Facial',
  'hollywood-laser-facial-1': 'Hollywood Laser Facial',
  'hydraboost-1': 'Hydraboost',
  'laser-hair-removal': 'Laser Hair Removal',
  'Hydraboost': 'Hydraboost',
  'Skin Lift': 'Skin Lift',
  'Skin Refresh': 'Skin Refresh'
};

// Service name to Zenoti service ID mapping (global, shared with zenoti-modal.js)
const SERVICE_ID_MAP = {
  // Consultations
  'Virtual Consultation': '9557b111-db0b-44dc-a834-e64f21cdd357',
  "Doctor's Consultation (Online)": '9557b111-db0b-44dc-a834-e64f21cdd357',
  'Remote Consultation': '9557b111-db0b-44dc-a834-e64f21cdd357',
  'In Person Doctors Appointment': '6dbfbbde-6303-4f1e-b339-02dc9775689c',
  "Doctor's Consultation (In Person)": '6dbfbbde-6303-4f1e-b339-02dc9775689c',
  'Skin Analyzer': '618a47e5-5704-41b8-b6eb-34390afc4b9b',
  'Hair Analyzer': '881a0715-4b2c-4b8d-a14e-9f6aa1e3a951',
  'Online Follow Up': '35e276b0-a272-4618-9167-42b7dfbed698',
  'In Person Follow Up': '00442d0d-fbed-4825-ae8d-a8e4baa2bf74',

  // Premium Treatments (confirmed IDs)
  'Hydraboost': '01bb67ff-8ca3-4869-aff9-7e7345dee60b',
  'Skin Lift': '58df0ed9-0399-4fc4-b115-1d6c12876ec5',
  'Skin Refresh': '244ff92c-0bbc-4da0-9cd2-f6d5b9db2161',

  // Other Treatments (Real Zenoti Service IDs)
  '595 Laser': '59a16783-4874-4955-9778-cd579dd0c4e8',
  'Acne Cleanse': '6bb942cd-4930-40e6-b9e3-845c2cc2b69e',
  'Back Peel': '0269505f-1ffa-4c71-be61-dd44d2ab5678',
  'Bidens': 'ab4c6c9f-963f-432c-9444-4d22dae09337',
  'Body Cleanse': 'ccf7e7ae-37fe-402f-8c8b-78e8a79b8197',
  'Body Slim': '025e3aaa-7e72-4da0-a955-a2f8ef3a1a91',
  'Bodybrite': '169a529b-cc78-4312-b1f5-3da62a3121ec',
  'BodyBrite Peel': '5b4bac83-9b65-4f9a-a58a-723ae85211ac',
  'Carbon Laser Facial': '980a5e5b-3fd4-4971-8664-05ff50c3be8e',
  'Cortisone Shots': 'afa278d0-5f04-492c-bf7a-06604b8c24c9',
  'Custom Peel': 'ba1db7a4-96ce-4ce6-a3ce-9dcdcfdf6458',
  'Electrolase': 'bf6dfdcb-44a0-44bc-a533-1726b87cde28',
  'Essential Cleanse': '7ba71ca1-0c30-4c1d-a721-663cd603a45f',
  'Exoskin': '2bdb93b3-754b-4601-bd97-70130b11a7e4',
  'Face Filter': '2506a9f1-e3b6-4c67-bde4-c6b087be340f',
  'Faux Fascial': '5fee1066-4092-48b2-ae5e-81255622959c',
  'Fresh Eyes': 'c1e30455-abe5-4aeb-ae90-09628fcffc1e',
  'Glass Skin Facial': '5fee1066-4092-48b2-ae5e-81255622959c',
  'Hollywood Laser Facial': 'ee1acb4a-7224-420f-8fca-8722ab1dcede',
  'Laser Hair Removal': 'd5f801b2-8fcd-4b27-9733-171a5b99a67d'
};

// Returning patients: the Zenoti service each request is booked as. Keys are the "Book A Treatment"
// dropdown values and the follow-up category names. IDs checked on 2026-10-23 against Zenoti's own
// online catalog (remedy.zenoti.com webstore). A treatment missing here has no verified Zenoti
// service yet and keeps the old default; add it here once its Zenoti service is known.
const RETURNING_ZENOTI_SERVICE_IDS = {
  'Follow Up Consultation (In Person)': '00442d0d-fbed-4825-ae8d-a8e4baa2bf74', // Follow-up Consultation
  'Follow Up Consultation (Online)': '35e276b0-a272-4618-9167-42b7dfbed698', // Online Follow-up Consultation
  'essential-cleanse': '7ba71ca1-0c30-4c1d-a721-663cd603a45f',
  'acne-cleanse-1': '6bb942cd-4930-40e6-b9e3-845c2cc2b69e',
  '595-laser': '59a16783-4874-4955-9778-cd579dd0c4e8',
  'carbon-laser-facial-1': '980a5e5b-3fd4-4971-8664-05ff50c3be8e',
  'hollywood-laser-facial-1': 'ee1acb4a-7224-420f-8fca-8722ab1dcede',
  'hydraboost-1': '01bb67ff-8ca3-4869-aff9-7e7345dee60b',
  'skin-refresh': '244ff92c-0bbc-4da0-9cd2-f6d5b9db2161',
  'skin-lift': '58df0ed9-0399-4fc4-b115-1d6c12876ec5',
  'face-filter': '2506a9f1-e3b6-4c67-bde4-c6b087be340f',
  'glass-skin-facial-1': '5fee1066-4092-48b2-ae5e-81255622959c',
  'fresh-eyes': 'c1e30455-abe5-4aeb-ae90-09628fcffc1e',
  'back-peel': '0269505f-1ffa-4c71-be61-dd44d2ab5678',
  'tricopat': 'a0f062e6-d5f4-46cb-9fcc-74c19d66571c',
};
window.RETURNING_ZENOTI_SERVICE_IDS = RETURNING_ZENOTI_SERVICE_IDS;

// Expose mappings globally for use in other scripts
window.TREATMENT_SERVICE_MAP = TREATMENT_SERVICE_MAP;
window.SERVICE_ID_MAP = SERVICE_ID_MAP;

function isSkinBarIneligible(serviceName) {
  // Hair Analyzer and other services are now active and available across centers including Skin Bar
  return false;
}

function updateCenterAvailabilityForService(serviceName) {
  const centerSelect = document.getElementById('center-select');
  if (!centerSelect) return;

  const skinBarOption = centerSelect.querySelector(
    'option[value="b419772b-0c99-4bdc-b171-0a2250ef045f"]'
  );
  const skinBarRadio = document.querySelector(
    'input[name="center"][value="b419772b-0c99-4bdc-b171-0a2250ef045f"]'
  );

  // Always keep all 3 locations visible and enabled in dropdown & radios
  if (skinBarOption) {
    skinBarOption.hidden = false;
    skinBarOption.disabled = false;
  }
  if (skinBarRadio) {
    skinBarRadio.disabled = false;
    const label = document.querySelector(`label[for="${skinBarRadio.id}"]`);
    if (label) label.style.display = '';
  }

  // If Skin Bar is currently selected and service is not eligible (e.g. Doctor's Consultation)
  const currentCenter = (centerSelect ? centerSelect.value : null) || window.selectedCenter;
  const isSkinBarSelected = currentCenter === 'b419772b-0c99-4bdc-b171-0a2250ef045f';

  if (isSkinBarSelected && isSkinBarIneligible(serviceName)) {
    const noticeEl = document.getElementById('no-dates-notice');
    if (noticeEl) {
      noticeEl.textContent = "This service is not available at Skin Bar by Remedy. Please select Uptown BGC Clinic or Vertis North Clinic.";
      noticeEl.style.display = 'block';
    }
    if (window.spaBookingWidgetInstance && window.spaBookingWidgetInstance.dateInput) {
      window.spaBookingWidgetInstance.dateInput.clear();
      window.spaBookingWidgetInstance.dateInput.set('disable', []);
      window.spaBookingWidgetInstance.dateInput.set('enable', []);
      window.spaBookingWidgetInstance.dateInput.redraw();
      window.spaBookingWidgetInstance.resetAvailableTimes();
    }
  } else {
    // If not ineligible, hide any prior Skin Bar error
    const noticeEl = document.getElementById('no-dates-notice');
    if (noticeEl && noticeEl.textContent.includes('Skin Bar')) {
      noticeEl.style.display = 'none';
      noticeEl.textContent = '';
    }
  }
}

// Backward compatibility
function toggleCentersForHairAnalyzer(serviceName) {
  updateCenterAvailabilityForService(serviceName);
}


// Initialize booking journey event listeners
function initBookingJourney() {
  // Mobile and drawer close handlers
  document.querySelectorAll('.drawer__close__booking, .mobile-nav-close-btn').forEach(btn => {
    btn.addEventListener('click', function (event) {
      event.preventDefault();
      const triggerApp = document.getElementById('trigger-booking-app');
      if (triggerApp) {
        triggerApp.classList.remove('active');
      }
      if (typeof window.resetNavMenuState === 'function') {
        window.resetNavMenuState();
      }
    });
  });

  // Datetime panel back button handler (mobile)
  const datetimeBackBtn = document.getElementById('datetime-panel-back-btn');
  if (datetimeBackBtn) {
    datetimeBackBtn.addEventListener('click', function (event) {
      event.preventDefault();
      const datetimePanel = document.getElementById('datetime-panel');
      if (datetimePanel) {
        datetimePanel.style.display = 'none';
      }
      document.querySelectorAll('.tier-2-link').forEach(link => {
        link.classList.remove('active');
      });
      window.selectedDate = null;
      window.selectedTime = null;
      updateBookButtonState();
    });
  }

  // Tier 2 drawer back buttons handler (mobile)
  document.querySelectorAll('.tier-2-back-btn').forEach(btn => {
    btn.addEventListener('click', function (event) {
      event.preventDefault();
      document.querySelectorAll('.tier-2-drawer').forEach(drawer => {
        drawer.classList.add('is-closed');
        drawer.classList.remove('is-open');
        drawer.setAttribute('aria-hidden', 'true');
      });
      document.querySelectorAll('.tier-1-link').forEach(link => {
        link.classList.remove('active');
      });
      const datetimePanel = document.getElementById('datetime-panel');
      if (datetimePanel) {
        datetimePanel.style.display = 'none';
      }
      window.selectedDate = null;
      window.selectedTime = null;
      updateBookButtonState();
    });
  });

  // Overlay click to close
  const overlay = document.querySelector('.overlay-booking-container');
  if (overlay) {
    overlay.addEventListener('click', function (event) {
      event.preventDefault();
      const triggerApp = document.getElementById('trigger-booking-app');
      if (triggerApp) {
        triggerApp.classList.remove('active');
      }
      if (typeof window.resetNavMenuState === 'function') {
        window.resetNavMenuState();
      }
    });
  }

  const bookNowButtons = document.querySelectorAll('[href="#book-now"]');
  bookNowButtons.forEach(button => {
    button.addEventListener('click', function (event) {
      event.preventDefault();
    });
  });

  // Add event listeners for center selection (New & Returning Patient)
  const centerRadios = document.querySelectorAll('input[name="center"]');
  centerRadios.forEach(radio => {
    radio.addEventListener('change', function () {
      window.selectedCenter = this.value;
      const centerSelect = document.getElementById('center-select');
      if (centerSelect && centerSelect.value !== this.value) {
        centerSelect.value = this.value;
      }
      const centerName = window.centerNames[this.value] || 'Unknown Center';
      console.log(`Selected center: ${centerName} (${this.value})`);

      // Update visual state for all center options
      centerRadios.forEach(r => {
        const label = document.querySelector(`label[for="${r.id}"]`);
        if (r.checked) {
          label.classList.add('checked');
        } else {
          label.classList.remove('checked');
        }
      });

      // Reset date and time when center changes
      window.selectedDate = null;
      window.selectedTime = null;
      updateBookButtonState();

      // Update the SpaBookingWidget with the new center
      if (window.spaBookingWidgetInstance) {
        const selectedService = getSelectedService();
        const dataUrl = document.querySelector('.tier-2-link.active')?.closest('li[data-url]')?.getAttribute('data-url');
        window.spaBookingWidgetInstance.resetWidget(selectedService, window.selectedCenter, dataUrl);
      }
    });
  });

  // Initialize center selection visual state
  centerRadios.forEach(radio => {
    const label = document.querySelector(`label[for="${radio.id}"]`);
    if (radio.checked) {
      label.classList.add('checked');
    }
  });

  const treatmentSelectNew = document.getElementById('options_treatment_new');
  const treatmentSelectReturning = document.getElementById('options_treatment');
  if (treatmentSelectNew) {
    treatmentSelectNew.addEventListener('change', function (event) {
      const selectedTreatment = this.value;
      toggleCentersForHairAnalyzer(selectedTreatment);
      window.spaBookingWidgetInstance.selectedService = this.value;
      window.treatmentSelectedByNewUser = !!this.value;
      const selectedOption = this.options[this.selectedIndex];
      const treatmentTitle = selectedOption ? selectedOption.textContent : '';
      // Reset date and time when center changes
      window.selectedDate = null;
      window.selectedTime = null;
      updateBookButtonState();

      console.log('=== NEW PATIENT TREATMENT DROPDOWN CHANGE ===');
      console.log('Selected treatment value:', selectedTreatment);
      console.log('Selected treatment title:', treatmentTitle);
      console.log('Current patient type:', isNewPatientFlow() ? 'New Patient' : 'Returning Patient');
      console.log('Current window.selectedCenter:', window.selectedCenter);
      console.log('================================');

      // Update the SpaBookingWidget with the new selected service
      if (window.spaBookingWidgetInstance) {
        const dataUrl = document.querySelector('.tier-2-link.active')?.closest('li[data-url]')?.getAttribute('data-url');
        window.spaBookingWidgetInstance.resetWidget(selectedTreatment, window.selectedCenter, dataUrl);
      }
    })
  } else if (treatmentSelectReturning) {
    treatmentSelectReturning.addEventListener('change', function (event) {
      console.log('=== RETURNING PATIENT TREATMENT DROPDOWN CHANGE ===');
      const selectedTreatment = this.value;
      toggleCentersForHairAnalyzer(selectedTreatment);
      window.spaBookingWidgetInstance.selectedService = this.value;
      const selectedOption = this.options[this.selectedIndex];
      const treatmentTitle = selectedOption ? selectedOption.textContent : '';
      console.log('Selected treatment value:', selectedTreatment);
      console.log('Selected treatment title:', treatmentTitle);
      console.log('Current patient type:', isNewPatientFlow() ? 'New Patient' : 'Returning Patient');
      console.log('Current window.selectedCenter:', window.selectedCenter);
      console.log('================================');
    })

  } else {
    console.log('No treatment select new found');
  }

  const tier1Links = document.querySelectorAll('.tier-1-link');

  tier1Links.forEach(link => {
    link.addEventListener('click', function (event) {
      event.preventDefault();
      if (this.classList.contains('disabled')) {
        return;
      }

      const parentTier1 = this.closest('li[data-url^="/new-patient"], li[data-url^="/returning-patient"]');
      if (parentTier1) {
        const dataUrl = parentTier1.getAttribute('data-url');
        const isNewPatient = dataUrl.startsWith('/new-patient');
        console.log(`Patient type set from navigation: ${isNewPatient ? 'New Patient' : 'Returning Patient'}`);

        const isCurrentlyActive = this.classList.contains('active');
        const drawerTargetId = this.getAttribute('data-drawer-target');
        const drawerTarget = drawerTargetId ? document.getElementById(drawerTargetId) : null;

        // Close all tier-2 drawers first
        document.querySelectorAll('.tier-2-drawer').forEach(drawer => {
          drawer.classList.add('is-closed');
          drawer.setAttribute('aria-hidden', 'true');
        });

        // Remove active state from ALL tier-1 links
        const allTier1Links = document.querySelectorAll('.tier-1-link');
        allTier1Links.forEach(link => {
          link.classList.remove('active');
        });

        // Remove active state from ALL tier-2 links
        const allTier2Links = document.querySelectorAll('.tier-2-link');
        allTier2Links.forEach(link => {
          link.classList.remove('active');
        });

        // Hide datetime panel
        const datetimePanel = document.getElementById('datetime-panel');
        if (datetimePanel) {
          datetimePanel.style.display = 'none';
        }

        // If the link wasn't already active, make it active and open its drawer
        if (!isCurrentlyActive) {
          this.classList.add('active');

          // Toggle the drawer for this tier-1 link
          if (drawerTarget) {
            drawerTarget.classList.remove('is-closed');
            drawerTarget.setAttribute('aria-hidden', 'false');
            console.log(`Opened drawer: ${drawerTargetId}`);
          }

          // Reset the spa booking widget with the new patient type
          if (window.spaBookingWidgetInstance) {
            const selectedCenter = window.selectedCenter || 'b877b429-d984-4eee-8e14-83f5a4dd5348';
            const dataUrl = parentTier1.getAttribute('data-url');
            const isNewPatient = dataUrl.startsWith('/new-patient');
            window.spaBookingWidgetInstance.resetWidget(
              null,
              selectedCenter,
              dataUrl,
              isNewPatient
            );
            console.log('Reset widget with data URL:', parentTier1.getAttribute('data-url'));
          }
        } else {
          console.log('Deselected tier-1 option:', this.textContent.trim());
        }
      }
    });
  });

  // Add click handlers for tier-2 links
  const tier2Links = document.querySelectorAll('.tier-2-link');
  tier2Links.forEach(link => {
    link.addEventListener('click', function (event) {
      event.preventDefault();
      console.log("tier2 click")
      window.treatmentSelectedByNewUser = false;
      window.treatmentSelectedByUser = false;
      // A category click starts a fresh choice. Clear any treatment still showing in the dropdowns,
      // so what the customer sees always matches what gets booked.
      ['options_treatment_new', 'options_treatment'].forEach(id => {
        const treatmentDropdown = document.getElementById(id);
        if (treatmentDropdown) treatmentDropdown.value = '';
      });
      if (this.classList.contains('disabled')) {
        return;
      }

      const parentTier1 = this.closest('li[data-url^="/new-patient"], li[data-url^="/returning-patient"]');

      if (parentTier1) {
        const dataUrl = parentTier1.getAttribute('data-url');
        const isNewPatient = dataUrl.startsWith('/new-patient');
        console.log(`Patient type set from navigation: ${isNewPatient ? 'New Patient' : 'Returning Patient'}`);
        console.log('Active tier-1 data-url from tier-2 click:', getActiveTier1DataUrl());

        const isCurrentlyActive = this.classList.contains('active');

        // Remove active state from ALL tier-2 links across both patient types
        const allTier2Links = document.querySelectorAll('.tier-2-link');
        allTier2Links.forEach(link => {
          link.classList.remove('active');
        });

        // Show/hide datetime panel based on tier-2 selection
        const datetimePanel = document.getElementById('datetime-panel');

        // If the link wasn't already active, make it active (toggle behavior)
        if (!isCurrentlyActive) {
          this.classList.add('active');
          datetimePanel.style.display = 'block';

          // Reset selected date and time when switching services
          window.selectedDate = null;
          window.selectedTime = null;

          // Select default center and sync both dropdown & radios
          const defaultCenterId = 'b877b429-d984-4eee-8e14-83f5a4dd5348';
          const centerSelect = document.getElementById('center-select');
          if (centerSelect) {
            centerSelect.value = defaultCenterId;
          }
          const uptownRadio = document.querySelector(`input[name="center"][value="${defaultCenterId}"]`);
          if (uptownRadio) {
            uptownRadio.checked = true;
          }
          window.selectedCenter = defaultCenterId;

          updateBookButtonState();

          // Initialize or reset the spa booking widget after showing the panel
          setTimeout(() => {
            const selectedService = this.textContent.trim();
            updateCenterAvailabilityForService(selectedService);
            const dataUrl = this.closest('li[data-url]')?.getAttribute('data-url');
            const selectedCenter = window.selectedCenter || defaultCenterId;

            console.log('Initializing booking widget with:', {
              service: selectedService,
              center: selectedCenter,
              dataUrl: dataUrl,
              element: document.getElementById('booking-section')
            });

            if (window.spaBookingWidgetInstance) {
              console.log('Resetting existing widget instance');
              const isNewPatient = dataUrl.startsWith('/new-patient');
              console.log('Patient type:', isNewPatient ? 'New Patient' : 'Returning Patient');
              window.spaBookingWidgetInstance.resetWidget(
                selectedService,
                selectedCenter,
                dataUrl,
                isNewPatient
              );
            } else if (typeof SpaBookingWidget !== 'undefined' && document.getElementById('booking-section')) {
              console.log('Creating new widget instance');
              console.log('Patient type:', isNewPatientFlow() ? 'New Patient' : 'Returning Patient');
              const widgetInstance = new SpaBookingWidget(
                selectedService,
                selectedCenter,
                dataUrl,
                isNewPatientFlow()
              );
            } else if (typeof flatpickr !== 'undefined') {
              flatpickr("#appointment-date", {
                minDate: "today",
                onChange: function (selectedDates, dateStr) {
                  console.log('Date selected:', dateStr);
                }
              });
            }
          }, 0);

        } else {
          datetimePanel.style.display = 'none';
          console.log('Deselected tier-2 option:', this.textContent.trim());
        }
      }
    });
  });

  // Add click handler for the Book Appointment button
  const bookAppointmentBtn = document.getElementById('book-appointment-btn');
  if (bookAppointmentBtn) {
    bookAppointmentBtn.addEventListener('click', function () {
      if (window.selectedDate && window.selectedTime) {
        handleBookAppointment();
      }
    });
  }

  findOrCreateZenotiGuest();
}

// Function to update the Book Appointment button state
function updateBookButtonState() {
  const bookAppointmentBtn = document.getElementById('book-appointment-btn');
  if (bookAppointmentBtn) {
    console.log('Updating book button state:', {
      date: window.selectedDate,
      time: window.selectedTime,
      center: window.selectedCenter,
      canBook: !!(window.selectedDate && window.selectedTime && window.selectedCenter)
    });

    if (window.selectedDate && window.selectedTime && window.selectedCenter) {
      bookAppointmentBtn.disabled = false;
      bookAppointmentBtn.title = `Book appointment for ${window.selectedDate} at ${window.selectedTime}`;
    } else {
      bookAppointmentBtn.disabled = true;
      if (!window.selectedDate || !window.selectedTime) {
        bookAppointmentBtn.title = 'Please select a date and time first';
      } else if (!window.selectedCenter) {
        bookAppointmentBtn.title = 'Please select a center first';
      }
    }
  }
}

// Expose the function globally so it can be called from spa-booking.js
window.updateBookButtonState = updateBookButtonState;

// Function to handle booking appointment
async function handleBookAppointment() {

  if (!window.selectedDate || !window.selectedTime) {
    console.log('Date and time must be selected');
    return;
  }

  // New Patient -> No Consultation: a treatment must be chosen, and the booking always uses it.
  if (isNoConsultTreatmentActive()) {
    const treatmentDropdown = document.getElementById('options_treatment_new');
    const pickedTreatment = treatmentDropdown ? treatmentDropdown.value : '';
    if (!pickedTreatment) {
      alert('Please select a treatment before booking.');
      return;
    }
    // Keep the Zenoti service in step with the dropdown so the Service ID always matches the product.
    if (window.spaBookingWidgetInstance) {
      window.spaBookingWidgetInstance.selectedService = TREATMENT_SERVICE_MAP[pickedTreatment] || pickedTreatment;
    }
  }

  // Returning patient -> Book A Treatment: a treatment must be chosen.
  const activeCategoryUrl = document.querySelector('.tier-2-link.active')?.closest('li[data-url]')?.getAttribute('data-url') || '';
  if (activeCategoryUrl.includes('book-a-treatment') && !document.getElementById('options_treatment')?.value) {
    alert('Please select a treatment before booking.');
    return;
  }

  const selectedService = getSelectedService();
  const productId = SERVICE_PRODUCT_MAP[selectedService];

  if (!productId) {
    console.error('No product ID found for service:', selectedService);
    alert('Service not available for booking. Please contact support.');
    return;
  }

  const selectedGender = '';

  // Get skin concerns from the select dropdown
  const skinConcernSelect = document.getElementById('options_skin_concern');
  const selectedConcerns = skinConcernSelect && skinConcernSelect.value ?
    [skinConcernSelect.value] : [];
  let concernsText;
  if (Array.isArray(selectedConcerns) && selectedConcerns.length > 0) {
    concernsText = `${selectedConcerns.join(', ')}`;
  } else if (typeof selectedConcerns === 'string') {
    concernsText = `${selectedConcerns}`;
  }

  const serviceId = window.spaBookingWidgetInstance ? window.spaBookingWidgetInstance.getServiceId() : null;

  const selectedTreatments = document.querySelector('#options_treatment')?.value || document.getElementById('options_treatment_new')?.value;


  console.log('Booking appointment:', {
    date: window.selectedDate,
    time: window.selectedTime,
    service: selectedService,
    serviceId: serviceId,
    productId: productId,
    customer: window.customerData,
    gender: selectedGender,
    skinConcerns: selectedConcerns,
    treatments: selectedTreatments,
    isNewPatient: isNewPatientFlow(),
    isReturning: window.isReturning
  });

  try {
    // Show loading state
    const bookAppointmentBtn = document.getElementById('book-appointment-btn');
    const originalText = bookAppointmentBtn.innerHTML;
    bookAppointmentBtn.disabled = true;
    bookAppointmentBtn.innerHTML = '<span class="btn-text">Adding to cart...</span>';

    // Prepare cart properties
    const cartProperties = {
      'Appointment Date': window.selectedDate,
      'Appointment Time': window.selectedTime,
      'Center': window.centerNames[window.selectedCenter] || 'Uptown BGC',
      'Center ID': window.selectedCenter,
      'Service': selectedService,
      'Service ID': serviceId
    };

    // Add customer info if available
    if (window.customerData) {
      cartProperties['Customer Email'] = window.customerData.email || '';
      cartProperties['Customer Name'] = `${window.customerData.first_name || ''} ${window.customerData.last_name || ''}`.trim();
    }

    // Add skin concerns for new patients
    if (concernsText) {
      cartProperties['Skin Concerns'] = concernsText;
    }

    // Add treatments for returning patients
    if (selectedTreatments) {
      cartProperties['Selected Treatments'] = selectedTreatments;
    }

    // Use the more reliable isNewPatientFlow() function instead of window.isNewPatient
    const isNewPatient = isNewPatientFlow();
    console.log(`Determined patient type from active tier-1: ${isNewPatient ? 'New Patient' : 'Returning Patient'}`);
    console.log('Active tier-1 data-url:', getActiveTier1DataUrl());

    // Returning patient Flow
    if (!isNewPatient) {
      // Returning patient - Trigger Zenoti modal
      console.log('Triggering Zenoti booking flow for returning patient');

      try {
        const serviceId = window.spaBookingWidgetInstance ? window.spaBookingWidgetInstance.getServiceId() : null;
        console.log('Service ID retrieved for modal:', serviceId);

        // Override service name if this is "Book A Treatment" with a specific treatment selected
        let finalServiceName = selectedService;
        if (selectedService === 'Book A Treatment' && selectedTreatments) {
          const treatmentName = TREATMENT_SERVICE_MAP[selectedTreatments];
          if (treatmentName) {
            finalServiceName = treatmentName;
            console.log('Override service name for treatment:', selectedService, '→', finalServiceName);
          }
        }

        // Store booking data for use in the modal
        window.zenotiBookingData = {
          date: window.selectedDate,
          time: window.selectedTime,
          service: finalServiceName,
          productId: productId,
          customer: window.customerData,
          gender: selectedGender,
          skinConcerns: selectedConcerns,
          treatments: selectedTreatments,
          center: window.centerNames[window.selectedCenter] || 'Uptown BGC',
          centerId: window.selectedCenter,
          serviceId: RETURNING_ZENOTI_SERVICE_IDS[selectedService === 'Book A Treatment' ? selectedTreatments : selectedService] || serviceId,
          cartProperties: cartProperties
        };

        console.log('Zenoti booking data stored:', window.zenotiBookingData);

        // Populate modal using the centralized function from zenoti-modal.js
        if (typeof window.initializeZenotiModal === 'function') {
          window.initializeZenotiModal();
        }

        // Show the modal
        const modal = document.getElementById('zenoti-booking-modal');
        modal.style.display = 'flex';

        // Reset button state
        bookAppointmentBtn.disabled = false;
        bookAppointmentBtn.innerHTML = originalText;

      } catch (error) {
        console.error('Error preparing Zenoti booking:', error);
        alert('There was an error preparing your booking. Please try again.');

        // Reset button state
        bookAppointmentBtn.disabled = false;
        bookAppointmentBtn.innerHTML = originalText;
      }

      return;
    }

    // New Patient - Use the Shopify order system
    const response = await fetch('/cart/add.js', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
      },
      body: JSON.stringify({
        items: [{
          id: productId,
          quantity: 1,
          properties: cartProperties
        }]
      })
    });

    if (!response.ok) {
      throw new Error(`HTTP error! status: ${response.status}`);
    }

    const cartData = await response.json();
    console.log('Product added to cart:', cartData);

    // Redirect to checkout
    window.location.href = '/checkout';

  } catch (error) {
    console.error('Error adding product to cart:', error);
    alert('There was an error adding the appointment to your cart. Please try again.');

    // Reset button state
    const bookAppointmentBtn = document.getElementById('book-appointment-btn');
    bookAppointmentBtn.disabled = false;
    bookAppointmentBtn.innerHTML = bookAppointmentBtn.innerHTML;
  }
}

// Function to get the active tier-1 data-url
function getActiveTier1DataUrl() {
  const activeTier1Link = document.querySelector('.tier-1-link.active');
  if (activeTier1Link) {
    const parentTier1 = activeTier1Link.closest('li[data-url^="/new-patient"], li[data-url^="/returning-patient"]');
    if (parentTier1) {
      return parentTier1.getAttribute('data-url');
    }
  }
  // Default to new-patient if no active tier-1 link is found
  return '/new-patient';
}

// Function to check if the current flow is for a new patient
function isNewPatientFlow() {
  const dataUrl = getActiveTier1DataUrl();
  return dataUrl.startsWith('/new-patient');
}

// True when the customer is in New Patient -> TREATMENTS (NO CONSULTATION REQUIRED)
function isNoConsultTreatmentActive() {
  const activeTier2Link = document.querySelector('.tier-2-link.active');
  const categoryUrl = activeTier2Link?.closest('li[data-url]')?.getAttribute('data-url') || '';
  return categoryUrl.includes('book-treatment-without');
}

// Function to get the currently selected service
function getSelectedService() {
  // New Patient -> No Consultation: the service is exactly what the treatment dropdown shows now.
  // Read it directly (no flag), so the price can never drift from what the customer sees.
  // Returns null when no treatment is chosen; handleBookAppointment then blocks the booking.
  if (isNoConsultTreatmentActive()) {
    const treatmentDropdown = document.getElementById('options_treatment_new');
    const treatmentHandle = treatmentDropdown ? treatmentDropdown.value : '';
    return treatmentHandle ? `${TREATMENT_SERVICE_MAP[treatmentHandle] || treatmentHandle}` : null;
  }

  // Check if user actually selected a treatment (not just default value)
  if (window.treatmentSelectedByUser) {
    const treatmentSelect = document.getElementById('options_treatment');
    if (treatmentSelect && treatmentSelect.value) {
      const treatmentHandle = treatmentSelect.value;
      const treatmentName = TREATMENT_SERVICE_MAP[treatmentHandle] || treatmentHandle;
      return `${treatmentName}`;
    }
  }

  // Fallback to tier-2 link method
  const activeTier2Link = document.querySelector('.tier-2-link.active');
  return activeTier2Link ? activeTier2Link.textContent.trim() : null;
}

// Function to set selected date (to be called from your date picker)
function setSelectedDate(date) {
  window.selectedDate = date;
  updateBookButtonState();
}

// Function to set selected time (to be called from your time picker)
function setSelectedTime(time) {
  window.selectedTime = time;
  updateBookButtonState();
}

function findOrCreateZenotiGuest() {
  // Handle both logged in and guest users
  const customerNewPanel = document.querySelector('.customer-new');
  const customerReturningPanel = document.querySelector('.customer-returning');

  if (window.customerData) {
    // Customer is logged in
    console.log('Customer is logged in. Proceeding with Zenoti guest check.', window.customerData);
    const isReturningCustomer = window.customerData.orders_count > 0;

    // Show/hide appropriate panels based on customer status
    if (isReturningCustomer && customerNewPanel) {
      customerNewPanel.style.display = 'none';
      if (customerReturningPanel) customerReturningPanel.style.display = 'block';
    } else {
      if (customerReturningPanel) customerReturningPanel.style.display = 'none';
      if (customerNewPanel) customerNewPanel.style.display = 'block';
    }

    const formData = new FormData();
    formData.append('email', window.customerData.email);
    formData.append('first_name', window.customerData.first_name);
    formData.append('last_name', window.customerData.last_name);
    formData.append('phone', window.customerData.phone);

    fetch('/apps/spa-booking/api/guests/find-or-create', {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json'
      },
      body: JSON.stringify(Object.fromEntries(formData))
    })
      .then(response => {
        if (!response.ok) {
          return response.text().then(text => {
            throw new Error('Network response was not ok. Server returned: ' + text);
          });
        }
        return response.json();
      })
      .then(data => {
        if (data.error) {
          console.error('Error finding or creating Zenoti guest:', data.error);
          window.zenotiCustomerData = null;
          handlePatientTypeSelection(false);
        } else {
          console.log('Successfully found or created Zenoti guest:', data);
          window.zenotiCustomerData = data;
          const isReturning = window.customerData.orders_count > 0;
          handlePatientTypeSelection(isReturning);
        }
      })
      .catch(error => {
        console.error('There was a problem with the fetch operation:', error);
        window.zenotiCustomerData = null;
        handlePatientTypeSelection(false);
      });
  } else {
    // Guest user - always treat as new patient
    console.log('Guest user detected. Treating as new patient.');
    window.zenotiCustomerData = null;

    // Show new patient panel, hide returning patient panel
    if (customerReturningPanel) customerReturningPanel.style.display = 'none';
    if (customerNewPanel) customerNewPanel.style.display = 'block';

    // Set up UI for new patient
    handlePatientTypeSelection(false);
  }
}

// Function to reset navigation menu state - clears all active states
window.resetNavMenuState = function () {
  console.log('Resetting navigation menu state');

  // Remove active states from all navigation links
  document.querySelectorAll('.tier-1-link, .tier-2-link, .tier-3-link, .tier-4-link').forEach(link => {
    link.classList.remove('active', 'disabled');
  });

  // Remove disabled states from all list items
  document.querySelectorAll('li[data-url]').forEach(li => {
    li.classList.remove('disabled');
  });

  // Close all tier-2 drawers
  document.querySelectorAll('.tier-2-drawer').forEach(drawer => {
    drawer.classList.add('is-closed');
    drawer.classList.remove('is-open');
    drawer.setAttribute('aria-hidden', 'true');
  });

  // Hide the datetime panel
  const datetimePanel = document.getElementById('datetime-panel');
  if (datetimePanel) {
    datetimePanel.style.display = 'none';
  }

  // Clear notice
  const noticeEl = document.getElementById('no-dates-notice');
  if (noticeEl) {
    noticeEl.style.display = 'none';
    noticeEl.textContent = '';
  }

  // Reset global state variables
  window.selectedDate = null;
  window.selectedTime = null;

  // Reset the spa booking widget if it exists
  if (window.spaBookingWidgetInstance) {
    console.log('Resetting spa booking widget instance');
    window.spaBookingWidgetInstance.resetAvailableTimes();
    if (window.spaBookingWidgetInstance.dateInput) {
      window.spaBookingWidgetInstance.dateInput.clear();
    }
  }

  // Update the book button state
  updateBookButtonState();

  console.log('Navigation menu state reset complete');
};

// Helper function to handle patient type selection in the UI
function handlePatientTypeSelection(isReturning) {
  console.log(`Patient type from active navigation: ${isNewPatientFlow() ? 'New Patient' : 'Returning Patient'}`);

  console.log("No longer disabling navigation!");

  // Reset any existing widget when patient type changes
  if (window.spaBookingWidgetInstance) {
    const selectedService = getSelectedService();
    if (selectedService) {
      const targetDataUrl = getActiveTier1DataUrl();
      console.log('Resetting widget due to patient type change', targetDataUrl);
      window.spaBookingWidgetInstance.resetWidget(
        selectedService,
        window.selectedCenter,
        targetDataUrl
      );
    }
  }
}

// Initialize booking journey when DOM is ready
document.addEventListener('DOMContentLoaded', initBookingJourney); 